import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Field } from "./AuthLayout";
import { Boxes, Check, ArrowRight, ArrowLeft } from "lucide-react";

const PLANS = [
  { key: "free", name: "Free", desc: "CRM, quotes & invoices. 1 user." },
  { key: "basic", name: "Basic", desc: "Adds sending, VAT, jobs, planner, expenses, reports & Alfie." },
  { key: "full", name: "Full", desc: "Adds team, time clock, materials & job margins." },
];

export default function Onboarding() {
  const { user, refetch } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", business_type: "Service business", plan: "free",
    phone: "", address: "", vat_number: "", vat_rate: 23, currency: "€",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [pricing, setPricing] = useState(null);
  useEffect(() => { api.get("/public/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  const priceOf = (key) => {
    if (!pricing) return "";
    return `${pricing.currency}${pricing.plans[key].price}${key === "free" ? "" : "/mo"}`;
  };

  if (user === null) return null;
  if (user && user.business_id) return <Navigate to="/app" replace />;
  if (user === false) return <Navigate to="/login" replace />;

  const submit = async () => {
    if (!form.name.trim()) { setStep(0); return toast.error("Please enter your business name"); }
    setLoading(true);
    try {
      await api.post("/onboarding", { ...form, vat_rate: Number(form.vat_rate) });
      await refetch();
      toast.success("Workspace created — just one more step.");
      nav("/billing");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setLoading(false);
  };

  const steps = ["Business", "Plan", "VAT & numbering"];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-10 px-4">
      <div className="flex items-center gap-2 font-display font-extrabold text-xl text-navy mb-8">
        <span className="h-9 w-9 rounded-full overflow-hidden"><img src="/amden-logo.png?v=2" alt="Amden One" className="h-full w-full object-cover" /></span>
        Amden<span className="text-blue-accent">One</span>
      </div>
      <div className="w-full max-w-xl amden-card p-8" data-testid="onboarding-wizard">
        <div className="flex items-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full grid place-items-center text-sm font-semibold ${i <= step ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <Check size={16} /> : i + 1}
              </div>
              <span className={`text-sm hidden sm:block ${i === step ? "text-navy font-semibold" : "text-slate-500"}`}>{s}</span>
              {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? "bg-blue-600" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-navy">Tell us about your business</h2>
            <Field label="Business name" required value={form.name} onChange={set("name")} data-testid="onboard-name" placeholder="e.g. Smith Plumbing" />
            <div>
              <label className="text-sm font-medium text-slate-700">Business type</label>
              <select value={form.business_type} onChange={set("business_type")} data-testid="onboard-type" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 bg-white">
                {["Sole trader", "Tradesperson", "Service business", "Small company"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <Field label="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="onboard-phone" />
            <Field label="Address (optional)" value={form.address} onChange={set("address")} data-testid="onboard-address" />
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <h2 className="text-xl font-bold text-navy">Choose your plan</h2>
            <p className="text-sm text-slate-500">You can change this anytime in settings.</p>
            {PLANS.map((p) => (
              <button key={p.key} type="button" onClick={() => setForm({ ...form, plan: p.key })} data-testid={`onboard-plan-${p.key}`}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${form.plan === p.key ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-navy">{p.name}</span>
                  <span className="font-mono font-semibold text-navy">{priceOf(p.key)}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{p.desc}</p>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-navy">VAT & numbering</h2>
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
              <p className="font-medium text-navy">VAT is charged at the standard 23% rate</p>
              <p className="text-sm text-slate-500">VAT is a regulatory charge applied to all quotes &amp; invoices. The rate is fixed at 23% and can't be changed.</p>
            </div>
            <Field label="VAT number (optional)" value={form.vat_number} onChange={set("vat_number")} data-testid="onboard-vat-number" />
            <div>
              <label className="text-sm font-medium text-slate-700">Currency</label>
              <select value={form.currency} onChange={set("currency")} data-testid="onboard-currency" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 bg-white">
                {["£", "€", "$"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
        )}

        <div className="flex justify-between mt-8">
          <button type="button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-0 transition-colors" data-testid="onboard-back">
            <ArrowLeft size={16} /> Back
          </button>
          {step < 2 ? (
            <button type="button" onClick={() => setStep(step + 1)} data-testid="onboard-next"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-navy text-white font-semibold hover:bg-slate-800 transition-colors">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={loading} data-testid="onboard-finish"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60">
              {loading ? "Creating…" : "Create workspace"} <Check size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
